import { calcLinesDiff, LinesDiff } from "bundles/BigqueryQueryViewer/lib/diff";
import { FixEdits } from "bundles/BigqueryQueryViewer/store";
import * as monaco from "monaco-editor";

export class QuickFixContentWidget implements monaco.editor.IContentWidget {
  private readonly id = "bigquery-ai-quick-fix";
  private monacoModule: typeof monaco;
  private editor: monaco.editor.IStandaloneCodeEditor;
  private domNode: HTMLDivElement;
  private innerNode: HTMLDivElement;
  private position: monaco.editor.IContentWidgetPosition | null = null;
  private hidden = true;
  private text = "";
  private range: monaco.IRange | undefined;
  private contextKey: monaco.editor.IContextKey<boolean>;
  private fixEdits?: FixEdits;
  private onFixComplete: () => void;
  private ignoreHide = false;
  private decorations: monaco.editor.IEditorDecorationsCollection;

  constructor(
    monacoModule: typeof monaco,
    editor: monaco.editor.IStandaloneCodeEditor,
    onFixComplete: () => void,
  ) {
    this.monacoModule = monacoModule;
    this.editor = editor;
    this.onFixComplete = onFixComplete;
    this.decorations = this.editor.createDecorationsCollection([]);

    this.domNode = document.createElement("div");
    this.domNode.style.border = "1px solid #ddd";
    this.domNode.style.backgroundColor = "rgb(255 255 255 / 0.95)";
    this.domNode.style.padding = "1px";
    this.domNode.style.cursor = "pointer";
    // monaco-editorのテキストのスタイル
    this.domNode.style.fontFamily = `Menlo, Monaco, Consolas, "Courier New", monospace`;
    this.domNode.style.fontWeight = "normal";
    this.domNode.style.fontSize = "14px";
    this.domNode.style.fontFeatureSettings = `"liga" 0, "calt" 0`;
    this.domNode.style.fontVariationSettings = "normal";
    this.domNode.style.lineHeight = "19px";
    this.domNode.style.letterSpacing = "0px";

    this.domNode.addEventListener("click", this.apply.bind(this));
    this.innerNode = document.createElement("div");
    this.domNode.appendChild(this.innerNode);
    this.innerNode.style.display = "grid";
    this.innerNode.style.gridTemplateColumns = "max-content max-content";

    this.editor.onDidChangeModel(this.hide.bind(this));
    this.editor.onDidChangeModelContent(this.hide.bind(this));

    this.contextKey = this.editor.createContextKey<boolean>(
      "quickFixContentWidgetVisible",
      false,
    );
    editor.addCommand(
      monaco.KeyCode.Tab,
      this.apply.bind(this),
      "!inlineSuggestionVisible && quickFixContentWidgetVisible",
    );
    editor.addCommand(
      monaco.KeyCode.Escape,
      this.hide.bind(this),
      "!inlineSuggestionVisible && quickFixContentWidgetVisible",
    );
    editor.onDidChangeCursorPosition(this.hide.bind(this));
  }

  getDomNode(): HTMLElement {
    return this.domNode;
  }

  getId(): string {
    return this.id;
  }

  getPosition(): monaco.editor.IContentWidgetPosition | null {
    if (this.hidden) {
      return null;
    }
    return this.position;
  }

  show(fixEdits: FixEdits) {
    this.fixEdits = fixEdits.slice();
    while (!this.showFirstFixEdits()) {
      this.fixEdits.shift();
      if (this.fixEdits.length == 0) {
        this.hide();
        return;
      }
    }
  }

  showFirstFixEdits() {
    const model = this.editor.getModel();
    if (!model) {
      return;
    }
    const fixEdit = this.fixEdits?.[0];
    if (!fixEdit) {
      return;
    }
    const original = fixEdit.original;
    const replacement = fixEdit.replacement;
    const lineNumber = fixEdit.lineNumber;
    const range = this.findRange(model, original, lineNumber);
    if (!range) {
      return;
    }
    this.text = replacement;
    this.range = range;
    this.hidden = false;
    this.position = {
      position: {
        lineNumber: this.range.startLineNumber,
        column: this.range.startColumn,
      },
      preference: [
        monaco.editor.ContentWidgetPositionPreference.BELOW,
        monaco.editor.ContentWidgetPositionPreference.ABOVE,
      ],
    };
    this.contextKey.set(true);
    const linesDiff = calcLinesDiff(
      original.replace("\r\n", "\n").split("\n"),
      replacement.replace("\r\n", "\n").split("\n"),
    );
    this.colorize(model, range, replacement, linesDiff);
    this.decorations.clear();
    const decorations: monaco.editor.IModelDeltaDecoration[] = [];
    for (const change of linesDiff.changes) {
      for (const innerChange of change.innerChanges) {
        decorations.push({
          range: {
            startLineNumber:
              range.startLineNumber +
              innerChange.originalRange.startLineNumber -
              1,
            startColumn:
              range.startColumn + innerChange.originalRange.startColumn - 1,
            endLineNumber:
              range.startLineNumber +
              innerChange.originalRange.endLineNumber -
              1,
            endColumn:
              range.startColumn + innerChange.originalRange.endColumn - 1,
          },
          options: {
            inlineClassName: "char-delete",
          },
        });
      }
    }
    this.decorations.append(decorations);
    return true;
  }

  private setInnerNodeHTML(html: string) {
    this.innerNode.innerHTML = `<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M1.5 1.5A.5.5 0 0 0 1 2v4.8a2.5 2.5 0 0 0 2.5 2.5h9.793l-3.347 3.346a.5.5 0 0 0 .708.708l4.2-4.2a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 8.3H3.5A1.5 1.5 0 0 1 2 6.8V2a.5.5 0 0 0-.5-.5"></path></svg><span style="display: inline-block">${html}</span>`;
  }

  private colorize(
    model: monaco.editor.IModel,
    range: monaco.IRange,
    replacement: string,
    linesDiff: LinesDiff,
  ) {
    // monaco-editor は全てのテキストに対してしかcolorrizeできないので置換後の全文字列に対して色を付ける
    const startText = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: range.startLineNumber,
      endColumn: range.startColumn,
    });
    const endText = model.getValueInRange({
      startLineNumber: range.endLineNumber,
      startColumn: range.endColumn,
      endLineNumber: model.getLineCount(),
      endColumn: model.getLineLength(model.getLineCount()) + 1,
    });
    const newText = `${startText}${replacement}${endText}`;
    this.monacoModule.editor
      .colorize(newText, "bigquery", {
        tabSize: 2,
      })
      .then((s) => {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(s, "text/html");
        // <span></span><br/> で1つの行を表す
        // 不要な内容を削除する
        let currentText = startText;
        const removingBrs: Element[] = [];
        for (const elem of parsedDocument.body.children) {
          switch (elem.tagName) {
            case "SPAN":
              for (const innerSpan of elem.children) {
                if (!innerSpan.textContent) {
                  continue;
                }
                const innerText = innerSpan.textContent.replace(/\u00A0/g, " ");
                if (currentText.startsWith(innerText)) {
                  innerSpan.textContent = "";
                  currentText = currentText.slice(innerText.length);
                } else if (innerText.startsWith(currentText)) {
                  innerSpan.textContent = innerSpan.textContent.slice(
                    currentText.length,
                  );
                  currentText = "";
                } else {
                  currentText = "";
                }
              }
              break;
            case "BR":
              if (currentText.startsWith("\n")) {
                currentText = currentText.slice(1);
                removingBrs.push(elem);
              } else if (currentText.startsWith("\r\n")) {
                currentText = currentText.slice(2);
                removingBrs.push(elem);
              }
              break;
          }
          if (currentText == "") {
            break;
          }
        }
        for (const br of removingBrs) {
          br.remove();
        }
        removingBrs.splice(0, removingBrs.length);
        currentText = endText;
        for (const elem of Array.from(parsedDocument.body.children).reverse()) {
          switch (elem.tagName) {
            case "SPAN":
              for (const innerSpan of Array.from(elem.children).reverse()) {
                if (!innerSpan.textContent) {
                  continue;
                }
                const innerText = innerSpan.textContent.replace(/\u00A0/g, " ");
                if (currentText.endsWith(innerText)) {
                  innerSpan.textContent = "";
                  currentText = currentText.slice(
                    0,
                    currentText.length - innerText.length,
                  );
                } else if (innerText.endsWith(currentText)) {
                  innerSpan.textContent = innerSpan.textContent.slice(
                    0,
                    innerText.length - currentText.length,
                  );
                  currentText = "";
                } else {
                  currentText = "";
                }
              }
              break;
            case "BR":
              if (currentText.endsWith("\r\n")) {
                currentText = currentText.slice(0, currentText.length - 2);
                removingBrs.push(elem);
              } else if (currentText.endsWith("\n")) {
                currentText = currentText.slice(0, currentText.length - 1);
                removingBrs.push(elem);
              } else break;
          }
          if (currentText == "") {
            break;
          }
        }
        for (const br of removingBrs) {
          br.remove();
        }

        // 更新された箇所の色付け
        let lineNumber = 1;
        let column = 1;
        for (const elem of parsedDocument.body.children) {
          switch (elem.tagName) {
            case "SPAN":
              const currentChildren = Array.from(elem.children);
              for (const innerSpan of currentChildren) {
                if (!innerSpan.textContent) {
                  continue;
                }
                for (const change of linesDiff.changes) {
                  for (const innerChange of change.innerChanges) {
                    if (
                      this.isRangeIntersected(
                        {
                          startLineNumber: lineNumber,
                          startColumn: column,
                          endLineNumber: lineNumber,
                          endColumn: column + innerSpan.textContent.length,
                        },
                        innerChange.modifiedRange,
                      )
                    ) {
                      let text: string = innerSpan.textContent;
                      if (column < innerChange.modifiedRange.startColumn) {
                        const cloned = innerSpan.cloneNode() as HTMLSpanElement;
                        cloned.textContent = text.substring(
                          0,
                          innerChange.modifiedRange.startColumn - column,
                        );
                        innerSpan.parentElement?.insertBefore(
                          cloned,
                          innerSpan,
                        );
                        text = text.substring(
                          innerChange.modifiedRange.startColumn - column,
                        );
                        column +=
                          innerChange.modifiedRange.startColumn - column;
                      }
                      const cloned = innerSpan.cloneNode() as HTMLSpanElement;
                      cloned.textContent = text.substring(
                        0,
                        innerChange.modifiedRange.endColumn -
                          innerChange.modifiedRange.startColumn,
                      );
                      cloned.classList.add("char-insert");
                      innerSpan.parentElement?.insertBefore(cloned, innerSpan);
                      text = text.substring(
                        innerChange.modifiedRange.endColumn -
                          innerChange.modifiedRange.startColumn,
                      );
                      column +=
                        innerChange.modifiedRange.endColumn -
                        innerChange.modifiedRange.startColumn;
                      innerSpan.textContent = text;
                    }
                  }
                }
                column += innerSpan.textContent.length;
                // innerSpan.classList.add("char-insert");
              }
              break;
            case "BR":
              lineNumber++;
              break;
          }
        }

        // UIの差し替え
        this.setInnerNodeHTML(parsedDocument.body.innerHTML);
        this.editor.layoutContentWidget(this);
      });
  }

  private isRangeIntersected(range1: monaco.IRange, range2: monaco.IRange) {
    return (
      (range1.startLineNumber < range2.endLineNumber &&
        range1.endLineNumber > range2.startLineNumber) ||
      (range1.startLineNumber == range2.startLineNumber &&
        range1.endLineNumber == range2.endLineNumber &&
        range1.startColumn < range2.endColumn &&
        range1.endColumn > range2.startColumn)
    );
  }

  private findRange(
    model: monaco.editor.IModel,
    original: string,
    lineNumber: number,
  ): monaco.IRange | undefined {
    if (original == "") {
      const lineLength = model.getLineLength(lineNumber);
      return {
        startLineNumber: lineNumber,
        startColumn: lineLength + 1,
        endLineNumber: lineNumber,
        endColumn: lineLength + 1,
      };
    }
    const lines = original.split("\n");
    let column = -1;
    for (const enumeratedLineNumber of this.enumerateLineNumbers(
      lineNumber,
      model.getLineCount(),
    )) {
      column = this.safeGetLineContent(model, enumeratedLineNumber).indexOf(
        lines[0],
      );
      if (column >= 0) {
        lineNumber = enumeratedLineNumber;
        break;
      }
    }
    if (column < 0) {
      return;
    }
    column++;
    let endLineNumber = lineNumber;
    let endColumn = column + original.length;
    if (lines.length > 1) {
      let index = 0;
      const restLines = lines.slice(1);
      for (const line of restLines) {
        if (index + 1 == restLines.length) {
          if (
            !this.safeGetLineContent(model, lineNumber + index + 1).startsWith(
              line,
            )
          ) {
            return;
          }
        } else {
          if (this.safeGetLineContent(model, lineNumber + index + 1) != line) {
            return;
          }
        }
        index++;
      }
      endLineNumber = lineNumber + restLines.length;
      endColumn = restLines[restLines.length - 1].length + 1;
    }
    return {
      startLineNumber: lineNumber,
      startColumn: column,
      endLineNumber: endLineNumber,
      endColumn: endColumn,
    };
  }

  private safeGetLineContent(
    model: monaco.editor.IModel,
    lineNumber: number,
  ): string {
    try {
      return model.getLineContent(lineNumber);
    } catch (e) {
      console.error("Error getting line content:", e);
      return "";
    }
  }

  // lineNumberを起点に前後列を探せるように列挙する
  private *enumerateLineNumbers(lineNumber: number, lineCount: number) {
    yield lineNumber;
    let cursor1 = lineNumber;
    let cursor2 = lineNumber;
    while (true) {
      if (cursor1 > 1) {
        cursor1--;
        yield cursor1;
      }
      if (cursor2 < lineCount) {
        cursor2++;
        yield cursor2;
      }
      if (cursor1 == 1 && cursor2 == lineCount) {
        break;
      }
    }
  }

  hide() {
    if (this.ignoreHide) {
      return;
    }
    this.decorations.clear();
    this.text = "";
    this.range = undefined;
    this.hidden = true;
    this.editor.layoutContentWidget(this);
    this.contextKey.set(false);
    if (this.fixEdits) {
      this.fixEdits = undefined;
      this.onFixComplete();
    }
  }

  isVisible() {
    return !this.hidden;
  }

  private apply() {
    if (!this.range) {
      return;
    }
    const range = this.range;
    this.ignoreHide = true;
    this.editor.getModel()?.pushEditOperations(
      this.editor.getSelections(),
      [
        {
          range: monaco.Range.lift(range),
          text: this.text,
        },
      ],
      () => [
        new monaco.Selection(
          range.endLineNumber,
          range.endColumn,
          range.endLineNumber,
          range.endColumn,
        ),
      ],
    );
    if (!this.fixEdits) {
      this.ignoreHide = false;
      this.hide();
      return;
    }
    this.fixEdits.shift();
    if (this.fixEdits.length > 0) {
      this.showFirstFixEdits();
      if (this.range) {
        this.editor.setPosition({
          lineNumber: this.range.startLineNumber,
          column: this.range.startColumn,
        });
        this.editor.revealLine(this.range.startLineNumber);
      }
      this.ignoreHide = false;
    } else {
      this.ignoreHide = false;
      this.hide();
    }
  }
}
